import type { EMPLOYMENT_TYPE } from 'cpool-constants';
import { EMPLOYMENT_TYPES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';

import getDefaultAzubi from '~/features/leadDetails/components/Overview/CustomerData/General/utils/defaultValues/getDefaultAzubi';
import getDefaultEmployee from '~/features/leadDetails/components/Overview/CustomerData/General/utils/defaultValues/getDefaultEmployee';
import getDefaultFreelancer from '~/features/leadDetails/components/Overview/CustomerData/General/utils/defaultValues/getDefaultFreelancer';
import getDefaultHousewife from '~/features/leadDetails/components/Overview/CustomerData/General/utils/defaultValues/getDefaultHousewife';
import getDefaultIndependent from '~/features/leadDetails/components/Overview/CustomerData/General/utils/defaultValues/getDefaultIndependent';
import getDefaultOfficial from '~/features/leadDetails/components/Overview/CustomerData/General/utils/defaultValues/getDefaultOfficial';
import getDefaultPensioner from '~/features/leadDetails/components/Overview/CustomerData/General/utils/defaultValues/getDefaultPensioner';
import getDefaultStudent from '~/features/leadDetails/components/Overview/CustomerData/General/utils/defaultValues/getDefaultStudent';
import getDefaultUnemployed from '~/features/leadDetails/components/Overview/CustomerData/General/utils/defaultValues/getDefaultUnemployed';
import getDefaultWorker from '~/features/leadDetails/components/Overview/CustomerData/General/utils/defaultValues/getDefaultWorker';
import type { Schema_BeschaeftigungForm } from '~/features/leadDetails/schemas/employment.schema';

const hasMatchingEmploymentTypeProperty = (
    employment: SchemaLoanRequestResolver_Get['antragsteller1']['beschaeftigung'],
) => {
    if (!employment) {
        return null;
    }
    switch (employment.beschaeftigungsart) {
        case EMPLOYMENT_TYPES.ANGESTELLTER:
            return employment.angestellter != null;
        case EMPLOYMENT_TYPES.ARBEITER:
            return employment.arbeiter != null;
        case EMPLOYMENT_TYPES.ARBEITSLOSER:
            return employment.arbeitsloser != null;
        case EMPLOYMENT_TYPES.BEAMTER:
            return employment.beamter != null;
        case EMPLOYMENT_TYPES.HAUSFRAU:
            return employment.hausfrau != null;
        case EMPLOYMENT_TYPES.FREIBERUFLER:
            return employment.freiberufler != null;
        case EMPLOYMENT_TYPES.RENTNER:
            return employment.rentner != null;
        case EMPLOYMENT_TYPES.SELBSTSTAENDIGER:
            return employment.selbststaendiger != null;
        case EMPLOYMENT_TYPES.AZUBI:
            return employment.azubi != null;
        case EMPLOYMENT_TYPES.STUDENT:
            return employment.student != null;
        default:
            return null;
    }
};

const getDefaultValues = (
    type: string,
): {
    beschaeftigung: Schema_BeschaeftigungForm;
} | null => {
    switch (type) {
        case EMPLOYMENT_TYPES.ANGESTELLTER:
            return getDefaultEmployee();
        case EMPLOYMENT_TYPES.ARBEITER:
            return getDefaultWorker();
        case EMPLOYMENT_TYPES.ARBEITSLOSER:
            return getDefaultUnemployed();
        case EMPLOYMENT_TYPES.BEAMTER:
            return getDefaultOfficial();
        case EMPLOYMENT_TYPES.HAUSFRAU:
            return getDefaultHousewife();
        case EMPLOYMENT_TYPES.FREIBERUFLER:
            return getDefaultFreelancer();
        case EMPLOYMENT_TYPES.RENTNER:
            return getDefaultPensioner();
        case EMPLOYMENT_TYPES.SELBSTSTAENDIGER:
            return getDefaultIndependent();
        case EMPLOYMENT_TYPES.AZUBI:
            return getDefaultAzubi();
        case EMPLOYMENT_TYPES.STUDENT:
            return getDefaultStudent();
        default:
            return null;
    }
};

export const getEmployment = (lead: SchemaLoanRequestResolver_Get, secondApplicant: boolean | undefined) => {
    const currentEmployment = structuredClone(
        secondApplicant && lead.antragsteller2
            ? lead.antragsteller2.beschaeftigung
            : lead.antragsteller1.beschaeftigung,
    );

    const currentEmploymentType: EMPLOYMENT_TYPE | '' = currentEmployment?.beschaeftigungsart
        ? currentEmployment.beschaeftigungsart
        : '';

    const employmentData =
        currentEmployment != null &&
        currentEmployment.beschaeftigungsart &&
        !hasMatchingEmploymentTypeProperty(currentEmployment)
            ? getDefaultValues(currentEmploymentType)?.beschaeftigung
            : currentEmployment;

    return { currentEmploymentType, employmentData };
};
